import React, {useMemo} from 'react';
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import CellWJTFCs from "./CellWJTFCs";
import Box from "@mui/material/Box";
import {Checkbox, CircularProgress} from "@mui/material";
import Typography from "@mui/material/Typography";

const OrganizationWorkersCheckboxTable = ({data, markedWorkers, setMarkedWorkers, loading}) => {

    const handleToggle = (id, checked) => {
        setMarkedWorkers((prev) => {
            if (checked) {
                return {...prev, [id]: true};
            }
            const next = {...prev};
            delete next[id];
            return next;
        });
    };

    const columns = useMemo(
        () => [
            {
                header: 'Статус',
                accessorKey: 'enabled',
                size: 48,
                maxSize: 48,
                grow: 0,
                enableEditing: false,
                enableColumnActions: false,
                Cell: ({row}) => <Checkbox
                    onChange={() => handleToggle(row.original.id, !markedWorkers[row.original.id])}
                    checked={Boolean(markedWorkers[row.original.id])}
                    sx={{'& .MuiSvgIcon-root': {fontSize: 20}}}
                />
            },
            {
                header: 'ФИО',
                accessorKey: 'workerTechnicalMap',
                grow: 1,
                enableEditing: false,
                Cell: ({cell}) => (
                    <Box sx={{whiteSpace: "nowrap"}}>
                        <CellWJTFCs cell={cell}/>
                    </Box>
                ),
            },
            {
                header: 'id',
                accessorKey: 'id',
                enableHiding: false,
                enableColumnActions: false,
                enableGrouping: false,
                enableEditing: false,
            },
        ],
        [markedWorkers, setMarkedWorkers],
    );

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                width: "100%",
            }}>
                <CircularProgress disableShrink size={32}/>
            </Box>
        );
    }

    return (
        <Box sx={{width: "100%", maxWidth: "100%", overflowX: "auto"}}>
        <MaterialReactTable
        layoutMode="grid"
        renderTopToolbar={({table}) => (
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: "4px",
                padding: "7px",
                width: "100%",
                boxSizing: "border-box",
                backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0
                    ? theme.palette.primary.light
                    : "transparent",
            }}>
                <MRT_ToolbarAlertBanner table={table}/>
                <Box sx={{display: "flex", flexWrap: "wrap", minWidth: 0}}>
                    <MRT_ToggleFiltersButton table={table}/>
                    <MRT_GlobalFilterTextField table={table}/>
                </Box>
            </Box>
        )}
        muiTablePaperProps={{
            elevation: 0,
            sx: {
                width: "100%",
                maxWidth: "100%",
                boxShadow: "none",
            },
        }}
        muiTableContainerProps={{
            sx: {
                maxWidth: "100%",
                overflowX: "auto",
            },
        }}
        muiTableBodyRowProps={() => ({
            sx: {
                height: '36px',
                backgroundColor: "white",
                "&:hover& td:after": {
                    display: "none",
                },
                "&.MuiTableRow-root.Mui-selected": {
                    backgroundColor: (theme) => theme.palette.primary.light70,
                    "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary.light50,
                    },
                },
                "&:hover": {
                    "& td:after": {
                        display: "none",
                    },
                    backgroundColor: (theme) => theme.palette.primary.stroke,
                },
                '&:nth-of-type(even)': {
                    "&.MuiTableRow-root.Mui-selected": {
                        backgroundColor: (theme) => theme.palette.primary.light,
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.primary.light50,
                        },
                    },
                    backgroundColor: (theme) => theme.palette.primary.lightGrey,
                    "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary.stroke,
                    },
                },
            },
        })}
        muiTableHeadCellProps={{
            sx: {
                border: '1px solid #EAEBEA',
                backgroundColor: "#F5F6FA",
                padding: "10px",
                paddingBottom: "0.2rem",
                paddingTop: "0.2rem",
            },
        }}
        muiTableBodyCellProps={({cell}) => ({
            sx: {
                p: cell.column.id === "enabled" ? 0 : '0px 0px 0px 10px',
                textAlign: cell.column.id === "enabled" ? "center" : "left",
                m: '0px',
                border: "none",
                "&:after": {
                    display: "none",
                },
                "&:not(:last-of-type)": {
                    borderRight: '1px solid #EAEBEA',
                },
            },
        })}
        muiSearchTextFieldProps={{
            placeholder: `Поиск`,
            sx: {
                paddingTop: "5px",
                paddingBottom: "0px",
                "& .MuiCollapse-root": {
                    borderRadius: '12px',
                    height: 40,
                    width: "100%",
                    maxWidth: 200,
                    "& .MuiSvgIcon-root": {
                        color: "#737478",
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: 0,
                    },
                },
            },
            variant: 'outlined',
        }}
        enableToolbarInternalActions={false}
        columns={columns}
        data={data}
        state={{showGlobalFilter: true}}
        rowHeight={13}
        columnSpacing={1}
        initialState={{
            columnVisibility: {
                id: false,
            },
            isFullScreen: false,
            density: "compact",
            pagination: {pageIndex: 0, pageSize: 50},
        }}
        enableEditing={false}
        enableRowSelection={false}
        getRowId={(originalRow) => originalRow.id}
        localization={MRT_Localization_RU}
        renderEmptyRowsFallback={() => (
            <Typography sx={{p: 2, textAlign: "center"}}>Нет работников</Typography>
        )}
    />
        </Box>
    );
};

export default OrganizationWorkersCheckboxTable;

export const normalizeOrganizationWorkers = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }
    return items.map((row) => ({
        id: row.id,
        workerTechnicalMap: row.workerTechnicalMap ?? row,
    }));
};
